import { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useRooftop } from '@store';
import {
  GetAdminContactsDocument,
  type GetAdminContactsQuery,
  type GetAdminContactsQueryVariables,
} from '@features/layout/queries/getAdminContacts.query.generated';

export type SupportContact = GetAdminContactsQuery['dealer_viewable_admin_info'][number];
export type ContactRole = 'market_manager' | 'account_manager' | 'sales_rep' | 'ar_rep';
const ROLES: ContactRole[] = ['market_manager', 'account_manager', 'sales_rep', 'ar_rep'];

type ContactState = { key: string; rows: SupportContact[]; loading: boolean; failed: boolean };

export function useSupportContacts() {
  const rooftop = useRooftop();
  const client = useApolloClient();
  // The rooftop carries the same DB-computed, inherited config the shell copies into
  // customerConfig. Reading it here keeps assignments tied to their customer even if
  // a switch has no config and leaves the previous global customerConfig in place.
  const config = rooftop?.effective_config as
    { hopdrive_contacts?: Partial<Record<ContactRole, number | null>> } | undefined;
  const configured = config?.hopdrive_contacts;
  const key = JSON.stringify([rooftop?.id, ...ROLES.map(role => configured?.[role] ?? null)]);
  const assignments = useMemo(() => {
    const [, ...ids] = JSON.parse(key) as Array<number | null>;
    return Object.fromEntries(ROLES.map((role, index) => [role, ids[index]])) as Record<ContactRole, number | null>;
  }, [key]);
  const userIds = useMemo(
    () => [...new Set(Object.values(assignments).filter((id): id is number => !!id))],
    [assignments]
  );
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<ContactState>({ key: '', rows: [], loading: false, failed: false });

  useEffect(() => {
    let current = true;
    setState({ key, rows: [], loading: userIds.length > 0, failed: false });
    if (userIds.length) {
      client
        .query<GetAdminContactsQuery, GetAdminContactsQueryVariables>({
          query: GetAdminContactsDocument,
          variables: { userIds },
          fetchPolicy: 'no-cache',
        })
        .then(({ data }) => {
          if (current) setState({ key, rows: data.dealer_viewable_admin_info, loading: false, failed: false });
        })
        .catch(() => {
          if (current) setState({ key, rows: [], loading: false, failed: true });
        });
    }
    return () => {
      current = false;
    };
  }, [client, key, userIds, attempt]);

  // Hide stale data during the render that changes customer, before the effect runs.
  const matching = state.key === key;
  return {
    assignments,
    rows: matching ? state.rows : [],
    loading: matching ? state.loading : userIds.length > 0,
    failed: matching && state.failed,
    retry: () => setAttempt(value => value + 1),
  };
}
